import { createSlice } from '@reduxjs/toolkit';
import type { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import type { WsMessagePayload } from '@skynet/data-ws-core';
import { parseResponse } from '@skynet/data-ws-core';
import type { ServerPrefix } from './types';
import { ServerModeCommands, ServerModes } from './types';

export interface ServerModeState {
  serverMode: ServerModes;
  shouldNotifyServerMode: boolean;
}

const initialState: ServerModeState = {
  serverMode: ServerModes.ReadWrite,
  shouldNotifyServerMode: false,
};

const knownModes: readonly unknown[] = Object.values(ServerModes);

const isServerMode = (mode: unknown): mode is ServerModes => knownModes.includes(mode);

/**
 * Creates a slice that tracks the server mode pushed over one WebSocket
 * channel. A push that changes the mode flags a notification until
 * `clearServerModeNotification` is dispatched; an unknown mode is ignored.
 */
export function createServerModeSlice<P extends ServerPrefix>(
  prefix: P,
  onMessage: ActionCreatorWithPayload<WsMessagePayload>,
) {
  const allowedCommands = Object.values(ServerModeCommands);

  return createSlice({
    name: `${prefix}ServerMode` as const,
    initialState,
    reducers: {
      clearServerModeNotification: (state) => {
        state.shouldNotifyServerMode = false;
      },
    },
    extraReducers: (builder) => {
      builder.addCase(onMessage, (state, action) => {
        const { json, skipProcessing } = parseResponse(action.payload, allowedCommands);

        if (skipProcessing || !json) return;

        const { command } = json;

        if (command === ServerModeCommands.ServerMode) {
          const { mode } = json;
          if (!isServerMode(mode)) return;

          const prevMode = state.serverMode;
          state.serverMode = mode;

          if (prevMode !== mode) {
            state.shouldNotifyServerMode = true;
          }
        }
      });
    },
  });
}
